/*
 * Created on Tue Jul 19 2022
 *
 * Copyright (c) 2022 Khor Chai Hong
 */

import { useEffect, useState, useRef } from 'react';
import { useLocation } from "react-router-dom";
import scrollTo from './scrollTo';
import Emitter from './emitter';
import {
    AccountTypes
} from '../constants'

export function useWindowDimensions() {
    let location = useLocation().pathname.substring(1);

    const [dimensions, setDimensions] = useState({
        windowWidth: 0,
        windowHeight: 0,
        isMobile: false
    })

    useEffect(() => {
        const updateDimensions = () => {
            setDimensions({
                windowWidth: window.innerWidth,
                windowHeight: window.innerHeight,
                isMobile: window.innerWidth <= 768
            });
            // console.log(window.innerWidth, window.innerHeight)
        };
        updateDimensions();
        window.addEventListener('resize', updateDimensions);
        return () => window.removeEventListener('resize', updateDimensions);
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        (location === 'signup' || location === 'find/tour' || location === 'here/contact-us' || location === 'here/manifesto' || location === 'here/how-it-works') && dimensions.isMobile ? document.body.style.overscrollBehavior = 'none' : document.body.style.overscrollBehavior = 'auto';
    }, [dimensions.isMobile, location])

    return dimensions;
}

export function usePrevState(value) {
    const ref = useRef();
    useEffect(() => {
        ref.current = value;
    });
    return ref.current;
}

export function useUserStatus() {
    let user;
    try {
        const localStoreUser = localStorage.getItem(`${process.env.REACT_APP_HOST}:user`);
        user = JSON.parse(localStoreUser)
    } catch (er) {

    }
    return user
}

export function useArtworkArticleStatus() {
    // closed/opened status
    const [artworkIsClosed, setArtworkIsClosed] = useState(true);
    const [itchArticleIsClosed, setItchArticleIsClosed] = useState(true);
    const [userArticleIsClosed, setUserArticleIsClosed] = useState(true);

    const initialState = {
        artwork: false,
        itchArticle: false,
        userArticle: false
    };
    // request to open status
    const [requested, setRequested] = useState(initialState);
    // allow to open status
    const [allowed, setAllowed] = useState(initialState);

    const setRequestedElement = (type) => {
        switch (type) {
            case 'artwork':
                setRequested({ ...initialState, artwork: true });
                break;
            case 'itch':
                setRequested({ ...initialState, itchArticle: true });
                break;
            case 'user':
                setRequested({ ...initialState, userArticle: true });
                break;
            default: return;
        }
    }

    const setClosingState = (type, state) => {
        switch (type) {
            case 'artwork':
                setArtworkIsClosed(state);
                break;
            case 'itch':
                setItchArticleIsClosed(state);
                break;
            case 'user':
                setUserArticleIsClosed(state);
                break;
            default: return;
        }
    }

    const reset = () => setRequested(initialState);

    // reset allowed element on every request
    useEffect(() => {
        setAllowed(initialState);
        // eslint-disable-next-line
    }, [requested])

    // console.log('requested', requested)
    useEffect(() => {
        // allow to open when other two are closed
        if (requested.artwork) {
            // console.log("artwork requested");
            if (itchArticleIsClosed && userArticleIsClosed) {
                setAllowed({ ...initialState, artwork: true });
            }
        }

        if (requested.itchArticle) {
            // console.log("itch requested", artworkIsClosed, userArticleIsClosed);
            if (artworkIsClosed && userArticleIsClosed) {
                setAllowed({ ...initialState, itchArticle: true });
            }
        }

        if (requested.userArticle) {
            // console.log("user requested");
            if (artworkIsClosed && itchArticleIsClosed && userArticleIsClosed) {
                setAllowed({ ...initialState, userArticle: true });
            }
        }
        // eslint-disable-next-line
    }, [requested, artworkIsClosed, itchArticleIsClosed, userArticleIsClosed])

    useEffect(() => {
        // opening state
        Emitter.on('openArtwork', () => setRequestedElement('artwork'));
        Emitter.on('openItchArticle', () => setRequestedElement('itch'));
        Emitter.on('openUserArticle', () => setRequestedElement('user'));

        // opened state
        Emitter.on('artworkIsOpened', () => setClosingState('artwork', false));
        Emitter.on('itchArticleIsOpened', () => setClosingState('itch', false));
        Emitter.on('userArticleIsOpened', () => setClosingState('user', false));

        // closed state
        Emitter.on('artworkIsClosed', () => setClosingState('artwork', true));
        Emitter.on('itchArticleIsClosed', () => setClosingState('itch', true));
        Emitter.on('userArticleIsClosed', () => setClosingState('user', true));

        // when close button is pressed, reset all
        Emitter.on('closeWithin', reset);
        return () => {
            Emitter.off('openArtwork', () => setRequestedElement('artwork'));
            Emitter.off('openItchArticle', () => setRequestedElement('itch'));
            Emitter.off('openUserArticle', () => setRequestedElement('user'));

            Emitter.off('artworkIsOpened', () => setClosingState('artwork', false));
            Emitter.off('itchArticleIsOpened', () => setClosingState('itch', false));
            Emitter.on('userArticleIsOpened', () => setClosingState('user', false));

            Emitter.off('artworkIsClosed', () => setClosingState('artwork', true));
            Emitter.off('itchArticleIsClosed', () => setClosingState('itch', true));
            Emitter.off('userArticleIsClosed', () => setClosingState('user', true));

            Emitter.off('closeWithin', reset);
        }
        // eslint-disable-next-line
    }, [])

    return {
        artworkIsClosed: artworkIsClosed,
        itchArticleIsClosed: itchArticleIsClosed,
        userArticleIsClosed: userArticleIsClosed,
        artworkAllowed: allowed.artwork,
        itchArticleAllowed: allowed.itchArticle,
        userArticleAllowed: allowed.userArticle
    };
}

export function ScrollToArticle(toRef, navHeight = 0) {
    const currentOffsetTop = toRef.current.getBoundingClientRect().top;
    const newPosition = Math.round(window.scrollY + currentOffsetTop - navHeight);
    // console.log("to", newPosition, "curr", window.scrollY, 'navHeight', navHeight);
    return scrollTo({
        ref: toRef,
        addOffsetTop: navHeight,
    });
}

export function WaitForClose(initialHeight, ref) {
    // there needs to be better way to check if the ref element height has restored to initial height
    if (initialHeight === 0) return;
    let element = ref ? ref.current : null;
    if (!element) return;
    return new Promise(resolve => {
        const maxCheckCount = 100
        let counter = 0
        const checkClosing = setInterval(() => {
            if (counter === maxCheckCount) {
                resolve(true)
            }
            counter++
            // console.log('WaitForClose > ', initialHeight, element, element.clientHeight)
            // check if current is equals to initial height
            if (initialHeight === element.clientHeight) {
                clearInterval(checkClosing);
                // console.log('done closing');
                resolve(true);
            }
        }, 25);
    })
}

export function nameConcat(name1, name2) {
    return [name1, name2].filter(o => o !== null && o !== undefined && o !== '').join(' ');
}

export function deriveUserName({
    accountType,
    firstName,
    lastName,
    companyName
}) {
    return accountType === AccountTypes.FashionCompany ? 
    companyName : nameConcat(firstName, lastName);
}

export function chunk(arr, size) {
    const out = []

    while (arr.length > 0) {
        const part = arr.splice(0, size)
        out.push(part)
        // console.log('part', part, out)
    }

    return out
}
